import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Card } from "@/components/ui/card";
import {
    Car, TrendingUp, DollarSign, Users, FileText, Package,
    Loader2, ArrowUpRight, Plus, ClipboardList, Wrench,
} from "lucide-react";

interface Analytics {
    kpis: {
        total_deals:     number;
        funded_deals:    number;
        pending_deals:   number;
        in_progress:     number;
        inventory_count: number;
        active_leads:    number;
        total_revenue:   number;
        avg_deal_value:  number;
        finance_rate:    number;
    };
    deals_by_status:  { status: string; count: number }[];
    monthly_volume:   { label: string; deals: number; revenue: number }[];
    deal_type_split:  { name: string; value: number }[];
    top_makes:        { make: string; count: number }[];
    recent_deals:     {
        deal_id: number; deal_status: string | null; deal_type: string | null;
        vYear: number | null; vMake: string | null; vModel: string | null;
        priceVehicle: string | null; deal_created_at: string;
    }[];
}

const STATUS_COLORS: Record<string, string> = {
    PENDING:   "#6366f1",
    VIEWED:    "#0ea5e9",
    COUNTERED: "#f59e0b",
    ACCEPTED:  "#10b981",
    DECLINED:  "#ef4444",
    CANCELLED: "#94a3b8",
    FUNDED:    "#059669",
};

const PIE_COLORS = ["#6366f1", "#0ea5e9"];

function money(n: number) {
    return n >= 1_000_000
        ? `$${(n / 1_000_000).toFixed(1)}M`
        : n >= 1_000
        ? `$${(n / 1_000).toFixed(0)}K`
        : `$${n}`;
}

function StatusBadge({ status }: { status: string | null }) {
    const s = status ?? "PENDING";
    const colors: Record<string, string> = {
        PENDING:   "bg-indigo-100 text-indigo-700",
        VIEWED:    "bg-sky-100 text-sky-700",
        COUNTERED: "bg-amber-100 text-amber-700",
        ACCEPTED:  "bg-emerald-100 text-emerald-700",
        DECLINED:  "bg-red-100 text-red-700",
        CANCELLED: "bg-gray-100 text-gray-500",
        FUNDED:    "bg-green-100 text-green-700",
    };
    return (
        <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${colors[s] ?? "bg-gray-100 text-gray-600"}`}>
            {s}
        </span>
    );
}

export default function DealerDashboardPage() {
    const [data,    setData]    = useState<Analytics | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API_URL}/dealers/analytics`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    if (!data) return (
        <div className="p-8 text-center text-muted-foreground">Failed to load analytics.</div>
    );

    const { kpis } = data;
    const topMakeMax = data.top_makes[0]?.count ?? 1;

    const kpiCards = [
        {
            label: "Total Deals",
            value: kpis.total_deals,
            sub: `${kpis.funded_deals} funded`,
            icon: FileText,
            color: "text-indigo-600",
            bg: "bg-indigo-50",
            to: "/dealer/deals",
        },
        {
            label: "Total Revenue",
            value: money(kpis.total_revenue),
            sub: `Avg ${money(kpis.avg_deal_value)}/deal`,
            icon: DollarSign,
            color: "text-emerald-600",
            bg: "bg-emerald-50",
            to: "/dealer/deals",
        },
        {
            label: "Active Inventory",
            value: kpis.inventory_count,
            sub: "units in stock",
            icon: Car,
            color: "text-blue-600",
            bg: "bg-blue-50",
            to: "/dealer/inventory",
        },
        {
            label: "Leads",
            value: kpis.active_leads,
            sub: "total leads",
            icon: Users,
            color: "text-violet-600",
            bg: "bg-violet-50",
            to: "/dealer/leads",
        },
        {
            label: "Finance Rate",
            value: `${kpis.finance_rate}%`,
            sub: "financed deals",
            icon: TrendingUp,
            color: "text-orange-600",
            bg: "bg-orange-50",
            to: "/dealer/finance",
        },
        {
            label: "In Progress",
            value: kpis.in_progress,
            sub: `${kpis.pending_deals} pending`,
            icon: Package,
            color: "text-sky-600",
            bg: "bg-sky-50",
            to: "/dealer/deals",
        },
    ];

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Dashboard</h1>
                    <p className="text-muted-foreground text-sm mt-0.5">Real-time dealership performance overview</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/dealer/inventory/add">
                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm hover:bg-muted transition-colors">
                            <Plus className="h-3.5 w-3.5" /> Add Vehicle
                        </button>
                    </Link>
                    <Link to="/dealer/deals">
                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 transition-colors">
                            <ClipboardList className="h-3.5 w-3.5" /> View Deals
                        </button>
                    </Link>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {kpiCards.map(({ label, value, sub, icon: Icon, color, bg, to }) => (
                    <Link key={label} to={to}>
                        <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer group">
                            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                                <Icon className={`h-5 w-5 ${color}`} />
                            </div>
                            <p className="text-xl font-bold">{value}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                            <p className="text-xs text-muted-foreground/70 mt-0.5">{sub}</p>
                        </Card>
                    </Link>
                ))}
            </div>

            {/* Charts row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Monthly Deal Volume — spans 2 cols */}
                <Card className="p-5 lg:col-span-2">
                    <h2 className="font-semibold mb-4">Deal Volume & Revenue (12 mo)</h2>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data.monthly_volume} barGap={2}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left"  tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                            <Tooltip
                                formatter={(value: number, name: string) =>
                                    name === "revenue" ? [`$${value.toLocaleString()}`, "Revenue"] : [value, "Deals"]
                                }
                            />
                            <Legend />
                            <Bar yAxisId="left"  dataKey="deals"   fill="#6366f1" radius={[3,3,0,0]} name="Deals" />
                            <Bar yAxisId="right" dataKey="revenue" fill="#10b981" radius={[3,3,0,0]} name="revenue" />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                {/* Deal Type Split */}
                <Card className="p-5">
                    <h2 className="font-semibold mb-4">Deal Type Split</h2>
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie
                                data={data.deal_type_split}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={80}
                                paddingAngle={3}
                                dataKey="value"
                                label={({ name, value }) => `${name}: ${value}`}
                                labelLine={false}
                            >
                                {data.deal_type_split.map((_, i) => (
                                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-4 mt-2">
                        {data.deal_type_split.map((item, i) => (
                            <div key={item.name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} />
                                {item.name} ({item.value})
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Deals by Status */}
                <Card className="p-5">
                    <h2 className="font-semibold mb-4">Deals by Status</h2>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={data.deals_by_status} layout="vertical" barSize={14}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="status" tick={{ fontSize: 11 }} width={72} />
                            <Tooltip />
                            <Bar dataKey="count" name="Deals" radius={[0,3,3,0]}>
                                {data.deals_by_status.map((entry) => (
                                    <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? "#94a3b8"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                {/* Top Makes */}
                <Card className="p-5 lg:col-span-2">
                    <h2 className="font-semibold mb-4">Top Vehicle Makes</h2>
                    {data.top_makes.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-10">No deal data yet</p>
                    ) : (
                        <div className="space-y-2.5">
                            {data.top_makes.map(({ make, count }) => (
                                <div key={make}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium">{make}</span>
                                        <span className="text-muted-foreground">{count} deal{count !== 1 ? "s" : ""}</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-indigo-500 rounded-full transition-all"
                                            style={{ width: `${(count / topMakeMax) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>

            {/* Recent Deals */}
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Recent Deals</h2>
                    <Link to="/dealer/deals" className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                        View all <ArrowUpRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="divide-y">
                    {data.recent_deals.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No deals yet</p>
                    ) : (
                        data.recent_deals.map((d) => (
                            <div key={d.deal_id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                                        <Car className="h-4 w-4 text-indigo-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">
                                            {d.vYear} {d.vMake} {d.vModel}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Deal #{d.deal_id} · {new Date(d.deal_created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className="text-sm font-mono font-medium">
                                        ${parseFloat(d.priceVehicle ?? "0").toLocaleString()}
                                    </span>
                                    <StatusBadge status={d.deal_status} />
                                    <span className="text-xs text-muted-foreground">
                                        {d.deal_type ?? "FINANCE"}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Card>

            {/* Quick Access */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { to: "/dealer/inventory/add",  icon: Plus,          label: "Add Vehicle",     color: "text-indigo-600" },
                    { to: "/dealer/leads",           icon: Users,         label: "View Leads",      color: "text-violet-600" },
                    { to: "/dealer/credit-apps",     icon: ClipboardList, label: "Credit Apps",     color: "text-emerald-600" },
                    { to: "/service/dashboard",      icon: Wrench,        label: "Service Portal",  color: "text-orange-600" },
                ].map(({ to, icon: Icon, label, color }) => (
                    <Link key={to} to={to}>
                        <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer">
                            <Icon className={`h-5 w-5 shrink-0 ${color}`} />
                            <span className="text-sm font-medium">{label}</span>
                        </Card>
                    </Link>
                ))}
            </div>
        </div>
    );
}
